/* eslint-disable react/require-default-props */
import React, {forwardRef} from 'react';
import {
  AccessibilityState,
  Platform,
  StyleProp,
  TouchableOpacity as RNTouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {isNil} from 'lodash';

import testProperties from '/util/testing/testProperties';

interface TouchableOpacityProps {
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
  onLongPress?: () => void;
  testID?: string;
  accessible?: boolean;
  disabled?: boolean;
  accessibilityLabel?: string;
  overflow?: string;
  accessibilityState?: AccessibilityState;
  accessibilityRole?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  focusable?: boolean;
  webID?: string;
}

export const TouchableOpacity = forwardRef<
  React.ElementRef<typeof RNTouchableOpacity>,
  TouchableOpacityProps
>(
  (
    {
      testID,
      accessible,
      onPress,
      onLongPress,
      accessibilityState,
      onFocus,
      onBlur,
      focusable,
      accessibilityRole = 'button',
      accessibilityLabel,
      style,
      webID = undefined,
      ...props
    },
    ref,
  ) => {
    if (Platform.OS === 'web') {
      const isInteractive = !isNil(onPress) || !isNil(onLongPress);
      const webStyle = [style, isInteractive && {cursor: 'pointer'}];
      // On web, render as View with onClick to avoid nested <button> elements
      return (
        <View
          ref={ref as any}
          style={webStyle as any}
          accessible={accessible ?? true}
          accessibilityRole={accessibilityRole as any}
          {...(accessibilityState ? {accessibilityState} : {})}
          accessibilityLabel={accessibilityLabel}
          focusable={focusable ?? accessible}
          // @ts-ignore - onClick is web-specific
          onClick={onPress}
          onFocus={onFocus}
          onBlur={onBlur}
          nativeID={webID}
          id={webID}
          testID={testID}
          {...testProperties(testID, false)}
          {...props}
        />
      );
    } else {
      // On mobile, use native TouchableOpacity
      return (
        <RNTouchableOpacity
          ref={ref}
          style={style}
          accessibilityRole={accessibilityRole as any}
          {...(accessibilityState ? {accessibilityState} : {})}
          accessible={accessible ?? true}
          disabled={isNil(onPress) && isNil(onLongPress)}
          accessibilityLabel={accessibilityLabel as string | null}
          {...testProperties(testID, false)}
          testID={testID}
          onPress={onPress}
          onLongPress={onLongPress}
          focusable={focusable ?? accessible}
          onFocus={onFocus}
          onBlur={onBlur}
          {...props}
          activeOpacity={0.7}
        />
      );
    }
  },
);

TouchableOpacity.displayName = 'TouchableOpacity';

export default TouchableOpacity;
